import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Crown, Loader2, MessageCircle, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useRoomSync } from "../lib/useRoomSync";
import PlayGroupPickPack from "./PlayGroupPickPack";

const AVATAR_MAP = {
  "pink-w":   { img: "/assets/home/avatar-pink-w.png",   ring: "#FF0F7B" },
  "orange-w": { img: "/assets/home/avatar-orange-w.png", ring: "#FFB800" },
  "purple-w": { img: "/assets/home/avatar-purple-w.png", ring: "#7B61FF" },
  "red-m":    { img: "/assets/home/avatar-red-m.png",    ring: "#FF0F7B" },
  "blue-m":   { img: "/assets/home/avatar-blue-m.png",   ring: "#00C2FF" },
  "green-m":  { img: "/assets/home/avatar-green-m.png",  ring: "#3DD68C" },
};
const av = (id) => AVATAR_MAP[id]?.img;
const ring = (id) => AVATAR_MAP[id]?.ring || "#FFB800";

const QUICK_EMOJIS = ["🔥", "😂", "💀", "👀", "💖", "🤣"];

/**
 * Unified Group gameplay component — 3 phases:
 *   1. "vote"    — Question Time: show prompt, 2x3 voting grid, progress bar
 *   2. "results" — Live Results: crown + winner big avatar + 87% badge + bar chart
 *   3. "next"    — Next Round prompt: 2 angled cards + Keep Playing / Change Pack
 * Reactions chat-style feed slides in from a button at top-right.
 */
export default function PlayGroupGameplay({ code, currentPlayerId, isHost, onLeave }) {
  const [phase, setPhase] = useState("vote"); // 'vote' | 'results' | 'next'
  const [results, setResults] = useState(null);
  const [myVote, setMyVote] = useState(null);
  const [showReactions, setShowReactions] = useState(false);
  const [submittingNext, setSubmittingNext] = useState(false);
  const [showPackSwitcher, setShowPackSwitcher] = useState(false);

  // SSE-backed group room sync (~500ms latency vs old 2s poll).
  const { data: room, setData: setRoom, refresh: fetchRoom } = useRoomSync({
    code,
    endpoint: "group-rooms",
    fetchFn: useCallback(() => api.getGroupRoom(code), [code]),
  });

  // Mirror "if my vote already cast, reflect" from the previous fetch impl
  useEffect(() => {
    if (!room) return;
    const v = (room.votes || {})[currentPlayerId];
    if (v) setMyVote(v);
  }, [room, currentPlayerId]);

  // When all players voted → fetch results & switch to results phase
  useEffect(() => {
    if (!room || phase !== "vote") return;
    const total = (room.players || []).length;
    const cast = Object.keys(room.votes || {}).length;
    if (total > 0 && cast >= total) {
      api.resultsGroup(code).then((res) => {
        setResults(res);
        setPhase("results");
      }).catch(() => { /* poll again next tick */ });
    }
  }, [room, phase, code]);

  // Reset state at every round boundary (host advancing OR host switching pack).
  // Guests in 'results' phase need this to flip back to 'vote' once the host
  // moves the room forward — otherwise they get stuck on the old results.
  useEffect(() => {
    if (!room) return;
    setResults(null);
    setMyVote(null);
    setPhase("vote");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.round_number, room?.pack_id]);

  const handleVote = async (targetId) => {
    if (myVote) return;
    setMyVote(targetId);
    try {
      await api.voteGroup(code, { voter_id: currentPlayerId, target_id: targetId });
    } catch (e) {
      setMyVote(null);
      toast.error(e?.response?.data?.detail || "Couldn't cast vote");
    }
  };

  const handleNext = async () => {
    if (!isHost) {
      toast.error("Only the host can advance");
      return;
    }
    setSubmittingNext(true);
    try {
      await api.nextGroupRound(code);
      setResults(null);
      setMyVote(null);
      setPhase("vote");
      await fetchRoom();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load next round");
    } finally {
      setSubmittingNext(false);
    }
  };

  if (!room) {
    return (
      <div className="vc-bg min-h-screen flex items-center justify-center text-white/70" data-testid="group-game-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const players = room.players || [];
  const totalPlayers = players.length;
  const votesCast = Object.keys(room.votes || {}).length;
  const progressPct = totalPlayers > 0 ? (votesCast / totalPlayers) * 100 : 0;
  const prompt = (room.current_question || {}).prompt || "";

  return (
    <div className="vc-bg min-h-screen relative overflow-hidden" data-testid="group-game-screen">
      {/* Warm gold-orange bloom */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          top: 40, left: "50%", transform: "translateX(-50%)",
          width: 460, height: 460,
          background: "radial-gradient(circle, rgba(255, 184, 0, 0.30) 0%, rgba(255, 138, 0, 0.14) 38%, transparent 70%)",
          filter: "blur(50px)", zIndex: 0,
        }}
        aria-hidden
      />

      <main className="relative z-[5] max-w-md mx-auto px-5 pt-5 pb-12 min-h-screen flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={onLeave}
            aria-label="Leave"
            data-testid="group-game-leave"
            className="w-10 h-10 rounded-full flex items-center justify-center text-white/85 hover:text-white transition active:scale-95"
            style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)", backdropFilter: "blur(8px)" }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div
            className="px-3 py-1 rounded-full text-[10px] font-black tracking-[0.22em]"
            style={{
              background: "rgba(255, 184, 0, 0.10)",
              border: "1.5px solid #FFB800",
              color: "#FFE082",
              boxShadow: "0 0 12px rgba(255, 184, 0, 0.32)",
            }}
          >
            🎉 ROUND {room.round_number || 1}
          </div>
          <button
            onClick={() => setShowReactions((v) => !v)}
            data-testid="group-game-reactions-toggle"
            aria-label="Reactions"
            className="w-10 h-10 rounded-full flex items-center justify-center text-white/85 hover:text-white transition active:scale-95 relative"
            style={{ background: "rgba(255, 184, 0, 0.10)", border: "1px solid rgba(255, 184, 0, 0.5)" }}
          >
            <MessageCircle className="w-[18px] h-[18px]" style={{ color: "#FFE082" }} strokeWidth={2.2} />
            {(room.reactions || []).length > 0 && (
              <span
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-black flex items-center justify-center text-black"
                style={{ background: "#FFB800", boxShadow: "0 0 6px rgba(255, 184, 0, 0.6)" }}
              >
                {Math.min((room.reactions || []).length, 99)}
              </span>
            )}
          </button>
        </div>

        {/* PHASE: VOTE / RESULTS */}
        <AnimatePresence mode="wait">
          {phase === "vote" && (
            <motion.div
              key="vote"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
            >
              {/* Title */}
              <h1
                className="font-extrabold leading-[1.0] tracking-tight text-center mb-3"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: "clamp(1.7rem, 6.4vw, 2.0rem)",
                  letterSpacing: "-0.02em",
                  background: "linear-gradient(90deg, #FFF3C2 0%, #FFB800 55%, #FF8A00 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  textShadow: "0 0 1px rgba(255, 184, 0, 0.6)",
                  filter: "drop-shadow(0 1px 1.5px rgba(0, 0, 0, 0.55))",
                }}
                data-testid="group-game-title-vote"
              >
                Question Time 🔥
              </h1>

              {/* Prompt card — universal vc-q system */}
              <div
                className="vc-q-card mb-4"
                style={{
                  "--vc-q-rgb": "255, 184, 0",
                  padding: "32px 22px 28px",
                }}
                data-testid="group-game-prompt"
              >
                <div className="vc-q-label" style={{ color: "#FFE082" }}>
                  Most Likely To
                </div>
                <p className="vc-q-text vc-q-gap-label-to-text" style={{ fontSize: "clamp(24px, 5.4vw, 32px)" }}>
                  {prompt || "Loading prompt…"}
                </p>
              </div>

              {/* Progress bar */}
              <div className="mb-3" data-testid="group-game-progress">
                <div className="flex items-center justify-between mb-1.5 px-1">
                  <span className="text-white/70 text-[11.5px] font-bold">
                    {votesCast} of {totalPlayers} voted
                  </span>
                  {myVote && <span className="text-[#FFE082] text-[11px] font-extrabold">Vote locked ✓</span>}
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(255, 255, 255, 0.08)" }}>
                  <motion.div
                    className="h-full"
                    style={{
                      background: "linear-gradient(90deg, #FFE082 0%, #FFB800 50%, #FF8A00 100%)",
                      boxShadow: "0 0 12px rgba(255, 184, 0, 0.6)",
                    }}
                    animate={{ width: `${progressPct}%` }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                </div>
              </div>

              {/* Player voting grid */}
              <div className="grid grid-cols-3 gap-2.5 mt-4" data-testid="group-game-voting-grid">
                {players.map((p) => {
                  const isMe = p.id === currentPlayerId;
                  const myVoted = myVote === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => handleVote(p.id)}
                      disabled={!!myVote || isMe}
                      data-testid={`group-game-vote-${p.id}`}
                      className="rounded-2xl px-2 py-3 flex flex-col items-center transition active:scale-95 disabled:opacity-55 disabled:cursor-not-allowed"
                      style={{
                        background: myVoted ? "rgba(255, 184, 0, 0.18)" : "rgba(15, 8, 22, 0.55)",
                        border: `2px solid ${myVoted ? "#FFB800" : ring(p.avatar_id) + "60"}`,
                        boxShadow: myVoted ? "0 0 16px rgba(255, 184, 0, 0.55)" : "none",
                      }}
                    >
                      <div
                        className="w-14 h-14 rounded-full overflow-hidden mb-1.5"
                        style={{
                          border: `2.5px solid ${ring(p.avatar_id)}`,
                          boxShadow: `0 0 10px ${ring(p.avatar_id)}88`,
                        }}
                      >
                        {av(p.avatar_id) ? (
                          <img src={av(p.avatar_id)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white font-extrabold" style={{ background: ring(p.avatar_id) + "33" }}>
                            {(p.name || "?")[0].toUpperCase()}
                          </div>
                        )}
                      </div>
                      <span className="text-white text-[11.5px] font-extrabold truncate w-full text-center">
                        {p.name}
                      </span>
                      {isMe && (
                        <span className="text-[#FFE082] text-[9px] font-black tracking-wider mt-0.5">YOU</span>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="text-center text-white/55 text-[11.5px] font-medium mt-4">
                {myVote
                  ? "Waiting for the squad to lock in…"
                  : "Tap who fits the prompt best 👀"}
              </div>

              {isHost && (
                <div className="text-center mt-3">
                  <button
                    onClick={() => setShowPackSwitcher(true)}
                    data-testid="group-game-change-pack-vote"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold transition active:scale-95"
                    style={{
                      background: "rgba(255, 184, 0, 0.08)",
                      border: "1px solid rgba(255, 184, 0, 0.4)",
                      color: "#FFE082",
                    }}
                  >
                    <RefreshCw className="w-3 h-3" strokeWidth={2.5} />
                    Switch Pack
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {phase === "results" && results && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
            >
              <h1
                className="font-extrabold leading-[1.0] tracking-tight text-center mb-2"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: "clamp(1.85rem, 7vw, 2.2rem)",
                  letterSpacing: "-0.02em",
                  background: "linear-gradient(90deg, #FFF3C2 0%, #FFB800 55%, #FF8A00 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  textShadow: "0 0 1px rgba(255, 184, 0, 0.6)",
                  filter: "drop-shadow(0 1px 1.5px rgba(0, 0, 0, 0.55))",
                }}
                data-testid="group-game-title-results"
              >
                And the winner is…
              </h1>
              <p className="text-center text-white/70 text-[12.5px] mb-4 px-3" data-testid="group-game-results-prompt">
                &ldquo;{results.prompt}&rdquo;
              </p>

              {/* Winner podium */}
              {results.winner && (
                <div className="flex flex-col items-center mb-5">
                  <Crown
                    className="w-10 h-10 mb-2"
                    style={{ color: "#FFB800", filter: "drop-shadow(0 0 12px rgba(255, 184, 0, 0.85))" }}
                    fill="#FFB800"
                  />
                  <motion.div
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 320, damping: 22 }}
                    className="w-[120px] h-[120px] rounded-full overflow-hidden relative"
                    style={{
                      border: "3.5px solid #FFB800",
                      boxShadow: "0 0 32px rgba(255, 184, 0, 0.7)",
                    }}
                    data-testid="group-game-winner-avatar"
                  >
                    {av(results.winner.avatar_id) ? (
                      <img src={av(results.winner.avatar_id)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white text-3xl font-extrabold" style={{ background: "rgba(255, 184, 0, 0.3)" }}>
                        {(results.winner.name || "?")[0].toUpperCase()}
                      </div>
                    )}
                  </motion.div>
                  <div className="mt-3 text-white text-[20px] font-extrabold" data-testid="group-game-winner-name">
                    {results.winner.name}
                  </div>
                  <div
                    className="inline-flex items-center gap-1.5 mt-1.5 px-3 py-1 rounded-full text-[12px] font-extrabold"
                    style={{
                      background: "linear-gradient(90deg, #FFE082 0%, #FFB800 100%)",
                      color: "#1a0e00",
                      boxShadow: "0 0 12px rgba(255, 184, 0, 0.55)",
                    }}
                    data-testid="group-game-winner-pct"
                  >
                    {results.winner.percent}% of votes
                  </div>
                </div>
              )}

              {/* Bar chart */}
              <div className="space-y-2 mb-5" data-testid="group-game-results-bars">
                {results.results.map((r, i) => (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + i * 0.06, duration: 0.35 }}
                    className="flex items-center gap-2.5"
                  >
                    <div
                      className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0"
                      style={{ border: `2px solid ${ring(r.avatar_id)}`, boxShadow: `0 0 6px ${ring(r.avatar_id)}66` }}
                    >
                      {av(r.avatar_id) ? (
                        <img src={av(r.avatar_id)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white text-[10px] font-extrabold" style={{ background: ring(r.avatar_id) + "33" }}>
                          {(r.name || "?")[0].toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between mb-0.5">
                        <span className="text-white text-[11.5px] font-bold truncate">{r.name}</span>
                        <span className="text-[#FFE082] text-[11px] font-black">{r.percent}%</span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255, 255, 255, 0.06)" }}>
                        <motion.div
                          className="h-full rounded-full"
                          style={{
                            background: i === 0
                              ? "linear-gradient(90deg, #FFE082, #FFB800, #FF8A00)"
                              : `linear-gradient(90deg, ${ring(r.avatar_id)}, ${ring(r.avatar_id)}88)`,
                            boxShadow: i === 0 ? "0 0 6px rgba(255, 184, 0, 0.6)" : "none",
                          }}
                          initial={{ width: 0 }}
                          animate={{ width: `${r.percent}%` }}
                          transition={{ duration: 0.7, delay: 0.15 + i * 0.06, ease: "easeOut" }}
                        />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {isHost ? (
                <>
                  <button
                    onClick={handleNext}
                    disabled={submittingNext}
                    data-testid="group-game-next-round"
                    className="vc-cta vc-cta-primary w-full rounded-full py-[16px] font-extrabold flex items-center justify-center gap-2"
                    style={{ fontSize: "16.5px" }}
                  >
                    {submittingNext ? "Loading next…" : (<>Keep Playing <ArrowRight className="w-5 h-5" strokeWidth={3} /></>)}
                  </button>
                  <button
                    onClick={() => setShowPackSwitcher(true)}
                    data-testid="group-game-change-pack"
                    className="w-full mt-2.5 rounded-full py-[12px] font-bold text-white/90 inline-flex items-center justify-center gap-2 transition active:scale-[0.98]"
                    style={{
                      background: "rgba(255, 184, 0, 0.08)",
                      border: "1.5px solid rgba(255, 184, 0, 0.55)",
                      fontSize: "14px",
                    }}
                  >
                    <RefreshCw className="w-4 h-4" style={{ color: "#FFE082" }} strokeWidth={2.4} />
                    <span style={{ color: "#FFE082" }}>Switch Pack</span>
                  </button>
                </>
              ) : (
                <div
                  className="rounded-2xl py-3 px-4 text-center"
                  style={{
                    background: "rgba(20, 14, 30, 0.55)",
                    border: "1.5px solid rgba(255, 184, 0, 0.45)",
                  }}
                  data-testid="group-game-guest-wait-next"
                >
                  <span className="text-white text-[13.5px] font-bold">
                    Waiting for the host to start the next round…
                  </span>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex-1" />
      </main>

      {/* Reactions drawer */}
      <ReactionsDrawer
        show={showReactions}
        onClose={() => setShowReactions(false)}
        room={room}
        code={code}
        currentPlayerId={currentPlayerId}
      />

      {/* Pack switcher overlay (host only) */}
      <AnimatePresence>
        {showPackSwitcher && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[70]"
            data-testid="group-game-pack-switcher-overlay"
          >
            <PlayGroupPickPack
              code={code}
              mode="switch"
              currentPackId={room?.pack_id}
              onBack={() => setShowPackSwitcher(false)}
              onLaunched={() => {
                setShowPackSwitcher(false);
                toast.success("Pack switched. Round 1 starting…");
                // Polling will pick up the new pack_id + round_number=1 and reset
                // all clients via the round-boundary useEffect.
                fetchRoom();
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------- Reactions sub-component ----------------
function ReactionsDrawer({ show, onClose, room, code, currentPlayerId }) {
  const [input, setInput] = useState("");
  const reactions = useMemo(() => (room?.reactions || []).slice().reverse(), [room]);

  const send = async (emoji) => {
    if (!emoji) return;
    try {
      await api.reactGroup(code, { player_id: currentPlayerId, emoji });
    } catch {
      toast.error("Couldn't send");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    setInput("");
    await send(text.slice(0, 8));
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          className="fixed inset-0 z-[60] flex items-end"
          style={{ background: "rgba(5, 2, 9, 0.7)", backdropFilter: "blur(10px)" }}
          data-testid="group-game-reactions-sheet"
        >
          <motion.div
            initial={{ y: 60 }}
            animate={{ y: 0 }}
            exit={{ y: 60 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md mx-auto rounded-t-[28px] p-5 pb-7"
            style={{
              background: "linear-gradient(180deg, rgba(28, 18, 12, 0.98) 0%, rgba(12, 8, 6, 0.98) 100%)",
              border: "1.5px solid rgba(255, 184, 0, 0.55)",
              boxShadow: "0 0 60px rgba(255, 184, 0, 0.35), 0 -10px 40px rgba(255, 138, 0, 0.25)",
              maxHeight: "78vh",
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="text-white text-[16px] font-extrabold inline-flex items-center gap-1.5">
                <span style={{ filter: "drop-shadow(0 0 6px #FFB800)" }}>🔥</span>
                Reactions
              </div>
              <button
                onClick={onClose}
                className="px-2.5 py-0.5 rounded-full text-white/70 text-[12px] font-bold hover:text-white"
                style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)" }}
              >
                Close
              </button>
            </div>

            {/* Feed */}
            <div
              className="overflow-y-auto mb-3 pr-1 space-y-1.5"
              style={{ maxHeight: "44vh" }}
              data-testid="group-game-reactions-feed"
            >
              {reactions.length === 0 ? (
                <div className="text-center py-6 text-white/40 text-[13px]">
                  No reactions yet. Drop a 🔥 to start.
                </div>
              ) : reactions.map((r, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded-2xl px-3 py-2"
                  style={{
                    background: "rgba(20, 14, 8, 0.55)",
                    border: `1px solid ${ring(r.avatar_id)}40`,
                  }}
                >
                  <div
                    className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0"
                    style={{ border: `1.5px solid ${ring(r.avatar_id)}` }}
                  >
                    {av(r.avatar_id) ? (
                      <img src={av(r.avatar_id)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white text-[10px] font-extrabold" style={{ background: ring(r.avatar_id) + "33" }}>
                        {(r.name || "?")[0].toUpperCase()}
                      </div>
                    )}
                  </div>
                  <span className="text-white text-[12.5px] font-bold flex-1 truncate">{r.name}</span>
                  <span className="text-[20px]" style={{ filter: "drop-shadow(0 0 4px #FFB800)" }}>{r.emoji}</span>
                </div>
              ))}
            </div>

            {/* Quick emoji bar */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {QUICK_EMOJIS.map((e) => (
                <button
                  key={e}
                  onClick={() => send(e)}
                  data-testid={`group-game-reaction-${e}`}
                  className="w-11 h-11 rounded-full text-[22px] transition active:scale-90"
                  style={{
                    background: "rgba(255, 184, 0, 0.12)",
                    border: "1.5px solid rgba(255, 184, 0, 0.45)",
                    boxShadow: "0 0 8px rgba(255, 184, 0, 0.28)",
                  }}
                >
                  {e}
                </button>
              ))}
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} className="flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                maxLength={8}
                placeholder="Type emoji…"
                data-testid="group-game-reactions-input"
                className="flex-1 rounded-full px-4 py-2.5 text-white text-[15px] font-bold focus:outline-none"
                style={{
                  background: "rgba(15, 8, 5, 0.7)",
                  border: "1.5px solid #FFB800",
                  boxShadow: "0 0 10px rgba(255, 184, 0, 0.32)",
                }}
              />
              <button
                type="submit"
                disabled={!input.trim()}
                data-testid="group-game-reactions-send"
                className="w-11 h-11 rounded-full flex items-center justify-center transition active:scale-95 disabled:opacity-50"
                style={{
                  background: "linear-gradient(180deg, #FFB800 0%, #FF8A00 100%)",
                  boxShadow: "0 0 14px rgba(255, 184, 0, 0.5)",
                }}
              >
                <Send className="w-4 h-4" style={{ color: "#1a0e00" }} strokeWidth={2.8} />
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
